import React from 'react';
import { NodeProps } from 'reactflow';
import type { OrphanTrayData } from '../types';

/**
 * Orphan Tray — Trailmap L1 (plan Task 12) DC3 surface.
 *
 * Artifacts with NULL source_turn_id (unbound to any turn) render here
 * — visible, not invisible. Per spec §6.5 DC3 + §8.7 a11y.
 *
 * When artifacts is empty, renders null (no DOM at all) so the canvas
 * doesn't show an empty tray decoration.
 */
const OrphanTrayNode: React.FC<NodeProps<OrphanTrayData>> = ({ data }) => {
  const { artifacts, onClick } = data;
  if (artifacts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label={`Orphan Tray: ${artifacts.length} unbound artifacts`}
      className={[
        // Task 16 polish (per Alan's smoke-test feedback): more prominent
        // visual weight so the tray reads as a real surface, not a
        // background decoration. Bigger padding, badged count chip in
        // the header row, ▣ glyph in the label so the orphan-tile
        // language stays consistent across surfaces.
        'flex flex-wrap gap-2 p-4 rounded-lg border-2 border-dashed shadow-sm',
        'bg-amber-50 border-amber-400 text-amber-900',
        'dark:bg-amber-900/30 dark:border-amber-400 dark:text-amber-50',
        'min-w-[280px] max-w-[420px]',
      ].join(' ')}
    >
      <div className="w-full flex items-center gap-2 pb-1 border-b border-amber-300/60 dark:border-amber-500/40">
        <span aria-hidden="true" className="text-base">▣</span>
        <span className="text-sm font-semibold uppercase tracking-wide">
          Unbound Artifacts
        </span>
        <span
          className={[
            'ml-auto inline-flex items-center justify-center min-w-[24px] h-6 px-2',
            'text-xs font-bold rounded-full',
            'bg-amber-300 text-amber-950',
            'dark:bg-amber-400 dark:text-amber-950',
          ].join(' ')}
        >
          {artifacts.length}
        </span>
      </div>
      {artifacts.map((a) => (
        <button
          key={a.id}
          id={`orphan-${a.id}`}
          data-artifact-id={a.id}
          role="button"
          aria-label={
            `Unbound artifact ▣: ${a.name} (${a.type}). Press Enter to open.`
          }
          onClick={() => onClick(a.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(a.id);
            }
          }}
          className={[
            'inline-flex items-center gap-1 px-2 py-1 rounded text-xs',
            'bg-white border border-amber-300 text-amber-900',
            'dark:bg-gray-800 dark:border-amber-500 dark:text-amber-200',
            'hover:bg-amber-100 dark:hover:bg-amber-900/40',
            'focus:outline-none focus:ring-1 focus:ring-amber-500',
          ].join(' ')}
        >
          <span aria-hidden="true">▣</span>
          <span className="truncate max-w-[100px]">{a.name}</span>
        </button>
      ))}
    </div>
  );
};

export default OrphanTrayNode;
